import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Lister from "../../components/Lister/Lister";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { DelayedCohort } from "../../models/DelayedCohort";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchAllDelayedCohorts } from "./DelayedCohorts.fuction";
import "./DelayedCohorts.scss";

const DelayedCohorts = () => {
  const { language } = useSession();
  const navigate = useNavigate();

  const [cohorts, setCohorts] = useState<DelayedCohort[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    fetchAllDelayedCohorts(setCohorts, setLoading, language);
  }, [language]);

  const filtered = useMemo(() => {
    return cohorts.filter((c) => {
      const matchesStatus = statusFilters.length === 0 || statusFilters.includes(c.status);
      const searchQuery = search.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        c.cohort_name.toLowerCase().includes(searchQuery) ||
        c.cohort_leader_name.toLowerCase().includes(searchQuery) ||
        (c.program_manager_name || "").toLowerCase().includes(searchQuery) ||
        c.active_sprint_name.toLowerCase().includes(searchQuery);
      return matchesStatus && matchesSearch;
    });
  }, [cohorts, statusFilters, search]);

  const items = useMemo(
    () =>
      filtered.map((c) => ({
        id: c.cohort_id,
        name: c.cohort_name,
        leader: c.cohort_leader_name,
        program_manager: c.program_manager_name || "N/A",
        end_date: c.dt_end,
        sprint: c.active_sprint_name,
        status: c.status,
        size: c.cohort_size,
        delayed: c.delayed_students_count,
        ratio: Math.round(c.delayed_students_ratio * 100),
      })),
    [filtered]
  );

  const columns = [
    {
      key: "id",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.ID"),
    },
    {
      key: "name",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.NAME"),
      render: (item: any) => <span className="fw-semibold">{item.name}</span>,
    },
    {
      key: "leader",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.LEADER"),
    },
    {
      key: "program_manager",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.PROGRAM_MANAGER"),
    },
    {
      key: "end_date",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.END_DATE"),
      sortType: "date",
      render: (item: any) => (
        <span title={formatDateHumanly(language, item.end_date, true)}>
          {formatDateHumanly(language, item.end_date)}
        </span>
      ),
    },
    {
      key: "sprint",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.CURRENT_SPRINT"),
    },
    {
      key: "status",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.STATUS"),
      render: (item: any) => (
        <span
          className={`badge ${
            item.status === "urgent" ? "bg-danger" : item.status === "late" ? "text-dark bg-warning" : "bg-success"
          }`}
        >
          {translate(language, `PAGES.DELAYED_COHORTS.STATUS.${item.status.toUpperCase()}`)}
        </span>
      ),
    },
    {
      key: "size",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.SIZE"),
      render: (item: any) => (
        <div>
          {item.size} <span className="ms-1 dst-icon-users-01" />
        </div>
      ),
    },
    {
      key: "delayed",
      header: translate(language, "PAGES.DELAYED_COHORTS.COLUMN.STUDENTS_LATE"),
      render: (item: any) => (
        <div style={{ minWidth: 120 }}>
          <div className="d-flex justify-content-between xs-regular">
            <span className="text-danger fw-semibold">{item.delayed}</span>
            <span className="text-muted">({item.ratio}%)</span>
          </div>
          <div className="progress" style={{ height: 6 }}>
            <div
              className={`progress-bar ${
                item.ratio > 70 ? "bg-danger" : item.ratio > 30 ? "bg-warning" : "bg-success"
              }`}
              style={{ width: `${item.ratio}%` }}
            />
          </div>
        </div>
      ),
    },
  ];

  const _handleStatusClick = (status: string) => {
    setStatusFilters((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  };
  return (
    <Container fluid className="p-regular p-3 p-md-4" id="delayed-cohorts">
      <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h2 className="mb-0">{translate(language, "PAGES.DELAYED_COHORTS.TITLE")}</h2>
        </div>
        {!loading && cohorts.length > 0 && (
          <div className="d-flex gap-2 flex-wrap">
            <span
              className={`badge bg-danger d-flex align-items-center gap-1 px-2 py-1 clickable ${
                statusFilters.includes("urgent") ? "active-filter" : ""
              }`}
              onClick={() => _handleStatusClick("urgent")}
            >
              <span className="dst-icon-alert-triangle" />
              {cohorts.filter((c) => c.status === "urgent").length}{" "}
              {translate(language, "PAGES.DELAYED_COHORTS.STATUS.URGENT")}
            </span>

            <span
              className={`badge text-dark bg-warning d-flex align-items-center gap-1 px-2 py-1 clickable ${
                statusFilters.includes("late") ? "active-filter" : ""
              }`}
              onClick={() => _handleStatusClick("late")}
            >
              <span className="dst-icon-clock" />
              {cohorts.filter((c) => c.status === "late").length}{" "}
              {translate(language, "PAGES.DELAYED_COHORTS.STATUS.LATE")}
            </span>

            <span
              className={`badge bg-success d-flex align-items-center gap-1 px-2 py-1 clickable ${
                statusFilters.includes("on_time") ? "active-filter" : ""
              }`}
              onClick={() => _handleStatusClick("on_time")}
            >
              <span className="dst-icon-check" />
              {cohorts.filter((c) => c.status === "on_time").length}{" "}
              {translate(language, "PAGES.DELAYED_COHORTS.STATUS.ON_TIME")}
            </span>
          </div>
        )}
      </div>

      <Row className="mb-3 g-2 align-items-center">
        <Col xs={12} sm={6} md={4}>
          <ListerSearch setSearchQuery={setSearch} timeDelay={300} showSearchIcon />
        </Col>
        <Col xs={12} sm={6} md={3}>
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <MultiFilterDropdown
                tags={[
                  { display: translate(language, "PAGES.DELAYED_COHORTS.STATUS.URGENT"), value: "urgent" },
                  { display: translate(language, "PAGES.DELAYED_COHORTS.STATUS.LATE"), value: "late" },
                  { display: translate(language, "PAGES.DELAYED_COHORTS.STATUS.ON_TIME"), value: "on_time" },
                ]}
                onSelectTags={(values) => setStatusFilters(values as string[])}
                inputTitle={translate(language, "PAGES.DELAYED_COHORTS.FILTER.STATUS")}
                language={language}
                defaultValue={statusFilters}
              />
            </div>
            {statusFilters.length > 0 && (
              <div
                className="ms-3 clickable dark-gray-font text-decoration-underline xs-regular"
                onClick={() => setStatusFilters([])}
              >
                {translate(language, "PAGES.DELAYED_COHORTS.FILTER.RESET")}
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Lister
        columns={columns}
        items={items}
        mock={loading}
        additionalButton={(item: any) => (
          <span
            className="clickable xs-regular"
            onClick={(event) => completeNavigate(event, navigate, `/cohort/${item.id}`)}
          >
            <span className="dst-icon-arrow-narrow-right s-regular" />
          </span>
        )}
        pagination
      />

      {!loading && filtered.length === 0 && cohorts.length > 0 && (
        <div className="text-center text-muted py-4 xs-regular">
          {translate(language, "PAGES.DELAYED_COHORTS.FILTER.NO_RESULTS")}
        </div>
      )}

      {!loading && cohorts.length === 0 && (
        <div className="text-center text-muted py-4 xs-regular">
          {translate(language, "PAGES.DELAYED_COHORTS.EMPTY")}
        </div>
      )}
    </Container>
  );
};

export default DelayedCohorts;
